import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Button,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  Pagination,
  SearchInput,
  SkeletonRows,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { useCan } from '@/auth/AuthContext';
import { listActivity, useQuery } from '@/data';
import { formatDateTime } from '@/lib/format';
import { ACTIVITY_FILTERS, activityIcon, actorLabel } from './activityIcons';

export function ActivityLogPage() {
  const fullAudit = useCan()('audit.read');
  const filters = ACTIVITY_FILTERS.filter((f) => fullAudit || !f.fullAudit);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [action, setAction] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const { data, error, loading, refreshing, reload } = useQuery(
    () => listActivity({ search: debounced, action: action || undefined, page, pageSize }),
    [debounced, action, page, pageSize],
  );

  const filtersDirty = !!(search.trim() || action);
  const clearFilters = () => {
    setSearch('');
    setDebounced('');
    setAction('');
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="Activity Log"
        subtitle={
          fullAudit
            ? 'Append-only audit trail of administrative events'
            : 'Business and branch activity (sign-in and staff events are visible to Super Admins only)'
        }
      />
      <div className="card">
        <FilterBar dirty={filtersDirty} onReset={clearFilters}>
          <SearchInput
            label="Search activity"
            placeholder="Search event, user or business…"
            value={search}
            onChange={setSearch}
            className="w-full sm:max-w-sm"
          />
          <FilterSelect
            label="Type"
            allLabel="All types"
            value={action}
            onChange={(v) => {
              setAction(v);
              setPage(1);
            }}
            options={filters}
            className="sm:w-52"
          />
        </FilterBar>
        {error ? (
          <ErrorState message={error.message} action={<Button variant="secondary" onClick={reload}>Try again</Button>} />
        ) : (
          <>
            <TableContainer label="Activity table">
              <Table className="min-w-[800px]" aria-busy={loading || refreshing}>
                <THead>
                  <tr>
                    <TH className="pl-5">Event</TH>
                    <TH>Business</TH>
                    <TH>User</TH>
                    <TH className="pr-5">Date / Time</TH>
                  </tr>
                </THead>
                <TBody className={refreshing ? 'opacity-60' : undefined}>
                  {loading ? (
                    <SkeletonRows rows={10} columns={4} />
                  ) : data && data.items.length > 0 ? (
                    data.items.map((e) => {
                      const Icon = activityIcon(e.action);
                      return (
                        <TR key={e.id} interactive>
                          <TD className="h-12 max-w-md whitespace-normal pl-5">
                            <span className="flex items-start gap-2.5">
                              <Icon size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-ink-muted" />
                              <span>
                                <span className="block font-medium leading-snug text-ink">{e.summary}</span>
                                <span className="mt-0.5 block font-mono text-[11px] uppercase tracking-wide text-ink-muted">
                                  {e.action}
                                </span>
                              </span>
                            </span>
                          </TD>
                          <TD className="h-12">
                            {e.business ? (
                              <Link to={`/pharmacies/${e.business.id}`} className="text-primary hover:underline">
                                {e.business.name}
                              </Link>
                            ) : (
                              <span className="text-ink-muted">—</span>
                            )}
                          </TD>
                          <TD className="h-12 text-ink-secondary">{actorLabel(e)}</TD>
                          <TD className="h-12 pr-5 tabular-nums text-ink-secondary">
                            <time dateTime={e.occurredAt}>{formatDateTime(e.occurredAt)}</time>
                          </TD>
                        </TR>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <EmptyState
                          icon={<History size={20} aria-hidden="true" />}
                          title="No activity found"
                          description={
                            filtersDirty ? 'Try a different search term or clear filters.' : 'Events will appear here as admins work.'
                          }
                        />
                      </td>
                    </tr>
                  )}
                </TBody>
              </Table>
            </TableContainer>
            {data && data.total > 0 && (
              <div className="border-t border-line">
                <Pagination
                  page={data.page}
                  pageSize={pageSize}
                  total={data.total}
                  onPageChange={setPage}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(1);
                  }}
                  itemLabel="events"
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
